import { useCountdown } from "@/hooks/useCountdown";
import { useUserGender } from "@/hooks/useUserGender";
import { functions } from "@/lib/firebaseConfig";
import { valueCheck, valueCheckForWomen } from "@/pages/api/listenIsAvailableRecommend";
import { DisabledStatus, Status, StatusType } from "@/types/status";
import { IdVerification } from "@/types/user";
import { checkIsNewUser, checkShowSuccessClaimBasicPlan } from "@/utils/common";
import { NAVBAR_LINK, NEW_USER_HOURS, STORAGE_KEY, USER_SEX } from "@/utils/enum";
import { firebaseTimestampToDate } from "@/utils/time";
import { addHours } from "date-fns";
import { httpsCallable } from "firebase/functions";
import { useRouter } from "next/router";
import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { RootContext } from "./rootContext";

interface OnboardingContextType {
  isNewOnboarding: boolean;
  setIsNewOnboarding: (value: boolean) => void;
  isAvailableRecommend: boolean;
  isUploadedID: boolean;
  ageConfirmation: boolean;
  handleSkip: () => void;
  loading: boolean;
  hasIdVerification: boolean;
  handleClaimBasicPlanFree: Function;
  status: string;
  isExpired: boolean;
  timeRemaining: string;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

interface OnboardingProviderProps {
  children: ReactNode;
}

export const OnboardingNewUserProvider = ({ children }: OnboardingProviderProps) => {
  const { state } = useContext(RootContext);

  const { user, member } = state || {};
  const [isNewOnboarding, setIsNewOnboarding] = useState(false);
  const [isAvailableRecommend, setIsAvailableRecommend] = useState(false);
  const [isUploadedID, setUploadedID] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasIdVerification, setHasIdVerification] = useState(false);
  const router = useRouter();
  const [status, setStatus] = useState<StatusType>(Status.Idle);
  const [isExpired, setIsExpired] = useState(false);
  const { isMaleUser } = useUserGender();

  const endTime = useMemo(() => {
    const createdAt = firebaseTimestampToDate(state?.user?.createdAt ?? new Date());
    return createdAt && createdAt instanceof Date
      ? addHours(createdAt, NEW_USER_HOURS)
      : "";
  }, [state?.user?.createdAt]);
  const timeRemaining = useCountdown(endTime, () => setIsExpired(true));

  useEffect(() => {
    if (isExpired && isMaleUser) setIsNewOnboarding(false);
  }, [isExpired])

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      setHasIdVerification(false);
      setUploadedID(false);
      setIsAvailableRecommend(false);
      setIsNewOnboarding(false);
      return;
    }

    setLoading(true);

    const checkOnboardingStatus = async () => {
      try {
        const hasIdVerification = member?.idVerifications?.length > 0;

        const hasUploadedID = member?.idVerifications?.some(
          (item: IdVerification) => !item.approvedAt && !item.rejectedAt
        ) ?? false;

        const isNewUser = checkIsNewUser(
          firebaseTimestampToDate(user.createdAt ?? new Date()),
          user.uid
        );

        const isAvailable = user.sex === USER_SEX.male
          ? await valueCheck(user)
          : user.isAvailable ? await valueCheckForWomen(user) : false;

        const isFullInfo = isAvailable && (hasIdVerification || user.ageConfirmation);

        let shouldShowOnboarding = false;
        if (isNewUser) {
          if (user.sex === USER_SEX.male) {
            shouldShowOnboarding = !isFullInfo || checkShowSuccessClaimBasicPlan(user.uid, Boolean(user?.verificationCampaignGranted));
          } else if (user.sex === USER_SEX.female) {
            shouldShowOnboarding = !isFullInfo;
          }
        }

        setHasIdVerification(hasIdVerification);
        setUploadedID(hasUploadedID);
        setIsAvailableRecommend(isAvailable);
        setIsNewOnboarding(shouldShowOnboarding);
      } catch {
        setHasIdVerification(false);
        setUploadedID(false);
        setIsAvailableRecommend(false);
        setIsNewOnboarding(false);
      } finally {
        setLoading(false);
      }
    };

    checkOnboardingStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, user?.updatedAt, member?.idVerifications]);

  const handleSkip = useCallback(() => {
    localStorage.setItem(`${STORAGE_KEY.SKIP_NEW_ONBOARDING}-${user?.uid}`, "true");
    setIsNewOnboarding(false);
  }, [user?.uid]);

  const handleClaimBasicPlanFree = useCallback(async () => {
    if (
      DisabledStatus.includes(status as Status) ||
      !user?.uid ||
      user?.sex !== USER_SEX.male
    ) return;
    setStatus(Status.Claiming);
    try {
      const claimVerificationCampaign = httpsCallable(functions, "claimVerificationCampaign");
      const result: any = await claimVerificationCampaign();
      if (Boolean(result?.data?.granted)) {
        setIsNewOnboarding(false);
        setStatus(Status.Claimed);
        localStorage.setItem(`${STORAGE_KEY.CLAIMED_BASIC_PLAN_FREE}-${user.uid}`, "true");
        router.replace(NAVBAR_LINK.claimedVerificationCampaign);
      } else {
        setStatus(Status.Idle);
      }
    } catch {
      setStatus(Status.Idle);
    }
  }, [status, user?.uid, user?.sex])

  const value: OnboardingContextType = {
    isNewOnboarding,
    setIsNewOnboarding,
    isAvailableRecommend,
    isUploadedID,
    ageConfirmation: Boolean(user?.ageConfirmation),
    handleSkip,
    loading,
    hasIdVerification,
    handleClaimBasicPlanFree,
    status,
    isExpired,
    timeRemaining
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = (): OnboardingContextType => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error("useOnboarding must be used within an OnboardingNewUserProvider");
  }
  return context;
};
